import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormCheck, FormControl, Modal } from 'react-bootstrap';
import { Calendar, CheckCircle, CheckCircleFill, PencilSquare, Person, PlusCircle, Trash } from 'react-bootstrap-icons';
import { uniqueId } from 'lodash';
import { highlightError } from '../../form-generator/helpers/utility';
import { DEFAULT_TASK_DUE_TIME, validateTasks as validateTaskFields } from '../../helpers/taskHelpers';
import SearchableSelect from '../common/SearchableSelect';

const Tasks = ({ tasks = [], disabled, onTasksChange, onTasksSubmit, appChoices, currentUserId, onValidationRef }) => {
  const isInternalUpdate = useRef(false);
  const originalTasksRef = useRef(null);

  const normalizeTask = (task = {}, existingId = null) => ({
    id: existingId || (task._id ? `task-${task._id}` : uniqueId()),
    _id: task._id,
    assignedTo: task.assignedTo?._id || task.assignedTo || '',
    assignedToName: task.assignedTo?.name || '',
    description: task.description || '',
    dueDate: task.dueDate ? moment(task.dueDate).format('YYYY-MM-DD') : '',
    dueTime: task.dueTime || (task.dueDate ? moment(task.dueDate).format('HH:mm') : DEFAULT_TASK_DUE_TIME),
    sendReminder: task.sendReminder || false,
    createdBy: task.createdBy?._id || task.createdBy || currentUserId,
    createdByName: task.createdBy?.name || '',
    completed: task.completed || false
  });

  const [localTasks, setLocalTasks] = useState(() => {
    const normalized = tasks?.length > 0 ? tasks.map(normalizeTask) : [];
    originalTasksRef.current = JSON.parse(JSON.stringify(normalized));
    return normalized;
  });

  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [isNewTask, setIsNewTask] = useState(false);

  const members = useMemo(() => {
    const membersChoice = appChoices?.find?.(c => c.key === 'members');
    const values = membersChoice?.values || [];
    return [...values].sort((a, b) => a.name.localeCompare(b.name));
  }, [appChoices]);

  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const normalized = tasks?.length > 0 ? tasks.map(task => {
      const existingTask = localTasks.find(lt => lt._id === task._id);
      return normalizeTask(task, existingTask?.id);
    }) : [];
    originalTasksRef.current = JSON.parse(JSON.stringify(normalized));
    setLocalTasks(normalized);
  }, [tasks, currentUserId, disabled]);

  const updateTasks = updatedTasks => {
    setLocalTasks(updatedTasks);
    if (onTasksChange) {
      isInternalUpdate.current = true;
      onTasksChange(updatedTasks);
    }
  };

  const handleTaskChange = (taskId, field, value) => {
    const updatedTasks = localTasks.map(task =>
      task.id === taskId ? { ...task, [field]: value } : task
    );
    updateTasks(updatedTasks);
    if (onTasksSubmit) {
      originalTasksRef.current = JSON.parse(JSON.stringify(updatedTasks));
      onTasksSubmit(updatedTasks);
    }
  };


  const handleCompletedToggle = (taskId) => {
    const updatedTasks = localTasks.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    updateTasks(updatedTasks);
    if (onTasksSubmit) {
      originalTasksRef.current = JSON.parse(JSON.stringify(updatedTasks));
      onTasksSubmit(updatedTasks);
    }
  };

  const validateTasksForm = () => {
    let hasError = false;
    localTasks.forEach(task => {
      clearFieldError(`task-description-${task.id}`);
      clearFieldError(`task-dueDate-${task.id}`);

      const descriptionField = document.getElementById(`task-description-${task.id}`);
      if (!task.description?.trim() && descriptionField) {
        highlightError(descriptionField, 'This field is required');
        hasError = true;
      }

      const dueDateField = document.getElementById(`task-dueDate-${task.id}`);
      if (task.sendReminder && !task.dueDate && dueDateField) {
        highlightError(dueDateField, 'Due date is required when sending a reminder');
        hasError = true;
      }
    });
    return !hasError;
  };

  const clearFieldError = fieldId => {
    const field = document.getElementById(fieldId);
    if (field) {
      field.classList.remove('border-danger');
      const errorMsg = field.parentNode.querySelector('[name="validationMsg"]');
      if (errorMsg) errorMsg.remove();
    }
  };

  const openTaskModal = (task, isNew = false) => {
    setActiveTask({ ...task });
    setIsNewTask(isNew);
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setActiveTask(null);
    setIsNewTask(false);
  };

  const saveTaskFromModal = () => {
    clearFieldError('task-modal-description');
    clearFieldError('task-modal-dueDate');

    try {
      validateTaskFields([activeTask]);
    } catch (error) {
      const message = error.message || '';
      if (message.includes('Description')) {
        const descriptionInput = document.getElementById('task-modal-description');
        if (descriptionInput) highlightError(descriptionInput, 'This field is required');
      }
      if (message.includes('due date')) {
        const dueDateInput = document.getElementById('task-modal-dueDate');
        if (dueDateInput) highlightError(dueDateInput, 'Due date is required when sending a reminder');
      }
      return;
    }
    const updatedTasks = isNewTask
      ? [...localTasks, activeTask]
      : localTasks.map(task =>
        task.id === activeTask.id ? activeTask : task
      );

    updateTasks(updatedTasks);
    if (onTasksSubmit) {
      originalTasksRef.current = JSON.parse(JSON.stringify(updatedTasks));
      onTasksSubmit(updatedTasks);
    }
    closeTaskModal();
  };

  const removeTask = taskId => {
    const updatedTasks = localTasks.filter(task => task.id !== taskId);
    updateTasks(updatedTasks);
    if (onTasksSubmit) {
      originalTasksRef.current = JSON.parse(JSON.stringify(updatedTasks));
      onTasksSubmit(updatedTasks);
    }

    if (expandedTaskId === taskId) setExpandedTaskId(null);
  };

  const getMemberName = memberId => members.find(m => m._id === memberId)?.name || '';

  const formatDate = dateStr => dateStr ? moment(dateStr).format('MMMM Do, YYYY') : '';

  const formatTime = timeStr => timeStr ? moment(timeStr, 'HH:mm').format('h:mm A') : '';

  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return '';
    const datePart = formatDate(dateStr);
    return timeStr ? `${datePart} at ${formatTime(timeStr)}` : datePart;
  };

  useEffect(() => {
    if (onValidationRef) onValidationRef.current = validateTasksForm;
  }, [onValidationRef, localTasks]);

  const stopPropagation = e => e.stopPropagation();

  const CompletionIcon = ({ completed, onClick }) => (
    <div className="d-flex align-items-center mr-3" onClick={onClick} style={{ cursor: 'pointer' }}>
      {completed ? <CheckCircleFill size={18} className="text-success" /> : <CheckCircle size={18} className="text-muted" />}
    </div>
  );

  const ActionButton = ({ icon: Icon, label, className, onClick }) => (
    <div className={`${className} hover-underline tinyFont`} onClick={onClick} style={{ cursor: 'pointer' }}>
      {Icon && <Icon size={label === 'Delete' ? 10 : 12} />} {label}
    </div>
  );

  const DateDisplay = ({ dateStr, timeStr }) => dateStr ? (
    <span className="d-flex align-items-center" style={{ color: '#000000', fontSize: '12px' }}>
      <Calendar size={12} className="mr-1" />
      {formatDateTime(dateStr, timeStr)}
    </span>
  ) : null;

  const MemberDisplay = ({ memberName }) => memberName ? (
    <span className="d-flex align-items-center text-muted" style={{ fontSize: '12px' }}>
      <Person size={12} className="mr-1" />
      {memberName}
    </span>
  ) : null;

  return (
    <div style={{ position: 'relative' }}>
      <h6 className="mb-2">Tasks</h6>

      <div>
        {localTasks.length > 0 ? localTasks.map(task => {
          const isExpanded = expandedTaskId === task.id;
          const assignedMember = getMemberName(task.assignedTo);

          return (
            <div key={task.id}>
              {!isExpanded ? (
                <div className="border bg-light rounded px-3 py-2 mt-1 midFont">
                  <div className="d-flex">
                    {!disabled && <CompletionIcon completed={task.completed} onClick={e => { stopPropagation(e); handleCompletedToggle(task.id); }} />}
                    <div className="mb-1 d-flex align-items-center justify-content-between flex-grow-1">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                          <DateDisplay dateStr={task.dueDate} timeStr={task.dueTime} />
                          <MemberDisplay memberName={assignedMember} />
                        </div>
                        <b className="mb-0">{task.description || 'No description'}</b>
                      </div>

                      {!disabled && (
                        <div className="d-flex align-items-start h-100" style={{ gap: '8px' }}>
                          <ActionButton icon={PencilSquare} label="Edit" className="text-primary" onClick={e => { stopPropagation(e); openTaskModal(task, false); }} />
                          <ActionButton icon={Trash} label="Delete" className="text-danger" onClick={e => { stopPropagation(e); removeTask(task.id); }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border bg-light rounded px-3 py-2 mt-1">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex flex-grow-1">
                      {!disabled && <CompletionIcon completed={task.completed} onClick={e => { stopPropagation(e); handleCompletedToggle(task.id); }} style={{ alignSelf: 'center' }} />}
                      <div className="flex-grow-1">
                        <div className="row mb-2">
                          <div className="col-6">
                            <label className="tinyFont text-muted mb-1 d-block">
                              <Calendar size={10} className="mr-1" /> Due Date
                              {task.sendReminder && <span className="text-danger"> *</span>}
                            </label>
                            {disabled ? (
                              <div className="midFont">{task.dueDate ? formatDateTime(task.dueDate, task.dueTime) : 'No date set'}</div>
                            ) : (
                              <div className="row no-gutters">
                                <div className="col-7 pr-1">
                                  <FormControl
                                    id={`task-dueDate-${task.id}`}
                                    type="date"
                                    size="sm"
                                    value={task.dueDate || ''}
                                    onChange={e => {
                                      handleTaskChange(task.id, 'dueDate', e.target.value);
                                      clearFieldError(`task-dueDate-${task.id}`);
                                    }}
                                    onClick={stopPropagation}
                                  />
                                </div>
                                <div className="col-5 pl-1">
                                  <FormControl type="time" size="sm" value={task.dueTime || DEFAULT_TASK_DUE_TIME} onChange={e => handleTaskChange(task.id, 'dueTime', e.target.value)} onClick={stopPropagation} />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="col-6">
                            <label className="tinyFont text-muted mb-1 d-block">
                              <Person size={10} className="mr-1" /> Assigned To
                            </label>
                            {disabled ? (
                              <div className="midFont">{assignedMember || 'Unassigned'}</div>
                            ) : (
                              <SearchableSelect
                                id={`task-assignedTo-${task.id}`}
                                size="sm"
                                options={members}
                                labelKey="name"
                                valueKey="_id"
                                value={task.assignedTo || ''}
                                onChange={val => { handleTaskChange(task.id, 'assignedTo', val); clearFieldError(`task-assignedTo-${task.id}`); }}
                                placeholder="Select member"
                                onMenuOpen={stopPropagation}
                              />
                            )}
                          </div>
                        </div>
                        {!disabled && (
                          <div className="mb-2">
                            <FormCheck className="d-flex align-items-center mb-0" onClick={stopPropagation}>
                              <FormCheck.Input
                                type="checkbox"
                                id={`task-sendReminder-${task.id}`}
                                checked={task.sendReminder || false}
                                onChange={e => {
                                  handleTaskChange(task.id, 'sendReminder', e.target.checked);
                                  if (!e.target.checked) clearFieldError(`task-dueDate-${task.id}`);
                                }}
                                className="mt-0"
                                style={{ width: '1.125rem', height: '1.125rem', flexShrink: 0 }}
                              />
                              <FormCheck.Label
                                htmlFor={`task-sendReminder-${task.id}`}
                                className="midFont mb-0 ml-2"
                                style={{ cursor: 'pointer', lineHeight: 1.4 }}
                              >
                                Send a Reminder
                              </FormCheck.Label>
                            </FormCheck>
                          </div>
                        )}
                        <label className="tinyFont text-muted mb-1 d-block">
                          Description {!disabled && <span className="text-danger">*</span>}
                        </label>
                        {disabled ? (
                          <div className="midFont">{task.description || 'No description'}</div>
                        ) : (
                          <FormControl
                            id={`task-description-${task.id}`}
                            as="textarea"
                            rows={2}
                            size="sm"
                            value={task.description}
                            onChange={e => { handleTaskChange(task.id, 'description', e.target.value); clearFieldError(`task-description-${task.id}`); }}
                            placeholder="Enter task description..."
                            className="midFont"
                            onClick={stopPropagation}
                          />
                        )}
                      </div>
                    </div>
                    {!disabled && (
                      <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        <div className="text-muted hover-underline tinyFont" onClick={e => { stopPropagation(e); setExpandedTaskId(null); }} style={{ cursor: 'pointer' }}>
                          Collapse
                        </div>
                        <ActionButton icon={Trash} label="Delete" className="text-danger" onClick={e => { stopPropagation(e); removeTask(task.id); }} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }) : <p className="text-secondary midFont my-1">No tasks to show!</p>}
      </div>

      {!disabled && (
        <div className="text-right d-flex justify-content-end" style={{ gap: '8px' }}>
          <Button onClick={() => openTaskModal(normalizeTask({ assignedTo: currentUserId }), true)} size="sm" variant="success" className="mt-2 d-flex align-items-center">
            <PlusCircle size={12} className="mr-1" /> New Task
          </Button>
        </div>
      )}

      {!disabled && activeTask && (
        <Modal show={showTaskModal} onHide={closeTaskModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{isNewTask ? 'Add Task' : 'Edit Task'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="tinyFont text-muted mb-1 d-block">
                <Calendar size={10} className="mr-1" /> Due Date
                {activeTask.sendReminder && <span className="text-danger"> *</span>}
              </label>
              <div className="row no-gutters">
                <div className="col-7 pr-1">
                  <FormControl
                    id="task-modal-dueDate"
                    type="date"
                    size="sm"
                    value={activeTask.dueDate || ''}
                    onChange={e => {
                      setActiveTask({ ...activeTask, dueDate: e.target.value });
                      clearFieldError('task-modal-dueDate');
                    }}
                  />
                </div>
                <div className="col-5 pl-1">
                  <FormControl type="time" size="sm" value={activeTask.dueTime || DEFAULT_TASK_DUE_TIME} onChange={e => setActiveTask({ ...activeTask, dueTime: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="tinyFont text-muted mb-1 d-block">
                <Person size={10} className="mr-1" /> Assigned To
              </label>
              <SearchableSelect
                size="sm"
                options={members}
                labelKey="name"
                valueKey="_id"
                value={activeTask.assignedTo || ''}
                onChange={val => setActiveTask({ ...activeTask, assignedTo: val })}
                placeholder="Select member"
              />
            </div>
            <div className="mb-3">
              <FormCheck className="d-flex align-items-center mb-0">
                <FormCheck.Input
                  type="checkbox"
                  id="task-modal-sendReminder"
                  checked={activeTask.sendReminder || false}
                  onChange={e => {
                    setActiveTask({ ...activeTask, sendReminder: e.target.checked });
                    if (!e.target.checked) clearFieldError('task-modal-dueDate');
                  }}
                  className="mt-0"
                  style={{ width: '1.125rem', height: '1.125rem', flexShrink: 0 }}
                />
                <FormCheck.Label
                  htmlFor="task-modal-sendReminder"
                  className="midFont mb-0 ml-2"
                  style={{ cursor: 'pointer', lineHeight: 1.4 }}
                >
                  Send a Reminder
                </FormCheck.Label>
              </FormCheck>
            </div>
            <div className="mb-2">
              <label className="tinyFont text-muted mb-1 d-block">
                Description <span className="text-danger">*</span>
              </label>
              <FormControl
                id="task-modal-description"
                as="textarea"
                rows={3}
                size="sm"
                value={activeTask.description || ''}
                onChange={e => {
                  setActiveTask({ ...activeTask, description: e.target.value });
                  clearFieldError('task-modal-description');
                }}
                placeholder="Enter task description..."
                className="midFont"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={closeTaskModal}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={saveTaskFromModal}>{isNewTask ? 'Add Task' : 'Save Changes'}</Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default Tasks;
